import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { formatCurrency, formatDateTime } from "@/lib/formatters";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";

interface ExportData {
  date: string;
  type: string;
  user: string;
  amount: number;
  currency: string;
  status: string;
  message: string;
}

interface ExportButtonProps {
  data: ExportData[];
  filename: string;
}

const ExportButton = ({ data, filename }: ExportButtonProps) => {
  const handleExport = async () => {
    if (data.length === 0) return;

    try {
      const headers = ["Date", "Type", "User", "Amount", "Status", "Message"];
      const csvContent = [
        headers.join(","),
        ...data.map(row => [
          formatDateTime(row.date),
          row.type,
          `"${row.user || ''}"`,
          formatCurrency(row.amount, row.currency),
          row.status || '',
          `"${(row.message || '').replace(/"/g, '""')}"`,
        ].join(","))
      ].join("\n");

      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `${filename}-${dateStr}.csv`;

      if (Capacitor.isNativePlatform()) {
        // Native mobile: save to cache and share
        const result = await Filesystem.writeFile({
          path: fileName,
          data: csvContent,
          directory: Directory.Cache,
          encoding: Encoding.UTF8,
        });

        await Share.share({
          title: 'Export CSV',
          url: result.uri,
        });

        toast.success(`Exported ${data.length} record${data.length === 1 ? '' : 's'}`);
      } else {
        // Web fallback: browser download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = "hidden";
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success(`Exported ${data.length} record${data.length === 1 ? '' : 's'} to CSV`);
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error("Failed to export data. Please try again.");
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleExport}
      disabled={data.length === 0}
      className="w-full"
    >
      <Download className="w-4 h-4 mr-2" />
      Export to CSV
    </Button>
  );
};

export default ExportButton;
